<template>
  <div v-if="problemData && problemData.id" class="problem-data-section">
    <div class="v-centerer problem-sheet__section-title-wrapper">
      <h2 class="basic-sub-title problem-sheet__section-title">
        {{ $t('message.DESCRIPTION') }}
      </h2>

      <ProblemTrust :trust="problemTrust" @click="App.showIA" />
    </div>

    <div class="problem-sheet__table">
      <div class="problem-sheet__row">
        <div class="problem-sheet__cell">
          <div class="problem-sheet__cell__wrapper">
            <FileChipList
              v-model="problemData.files"
              editable
              column
              upload-zone
              pathname="problems/"
              own-file-viewer
              display-files
              @input="editProblem('files')"
            />
          </div>
        </div>
      </div>
      <div v-for="(field, idx) in fieldProps" :key="field.key" class="problem-sheet__row">
        <div class="problem-sheet__cell">
          <div
            :id="`sheet-id-${idx}`"
            class="problem-sheet__cell__wrapper"
            @click="isMounted ? $refs[`field-${field.key}`][0].doFocus() : ''"
          >
            <EditableLabel
              :ref="`field-${field.key}`"
              v-model="problemData[field.key]"
              v-bind="field"
              :legend="$t(`message.${field.title}`)"
              :disabled="disabled"
              :placeholder="$t('message.CLICK_TO_TYPE')"
              @input="editProblem(field.key)"
            />
            <div
              v-if="displayHints"
              class="field-hint problem-data-section__hint"
              v-html="safeString(currentTeam.teamProblemHints[field.key])"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 5W1H section of the problem sheet
 * @vue-prop {Object} problem - Original problem to be displayed. If fetchData
 *                    is true, problem will be updated from server's data
 * @vue-prop {Boolean} disabled - if false and fetchData is also false, fields won't be editable
 *
 * @vue-event {String, *} sheetEdit - Emited when a value is updated so that any parent
 *                        can get the update
 * @vue-event {Object} pictureClick - Emitted if user clicked to edit the files
 */

import { cloneDeep, isEqual } from 'lodash';
// Vuex utils
import { mapState } from 'vuex';
import { EDIT_PROBLEM } from '@/store/modules/problem/action-types';
import { GET_ZONES } from '@/store/mutation-types';
// Components
import EditableLabel from '@/components/editable-label.vue';
import FileChipList from '@/components/file-system/file-chip-list.vue';
import ProblemTrust from '@/components/problem-sheet/problem-trust.vue';
// Utils
import { AutocompleteChipsTypes } from '@/utils/classes/AutocompleteChipsTypes';
import { treeAsArray, safeString } from '@/utils/main';
import { getProblemTrustIndex } from '@/utils/problem-utils';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';

export default {

  name: 'ProblemDataSection',

  components: {
    EditableLabel,
    FileChipList,
    ProblemTrust,
  },

  mixins: [mixinSocketHandler],

  props: {
    problem: {
      type: Object,
      required: true,
    },
    disabled: Boolean,
  },

  data() {
    return {
      AutocompleteChipsTypes,
      // Copy of 'problem' prop to be edited through the template
      problemData: { files: [] },
      // PictureCarousel must be rebuilt every time the access changes
      picturesKey: 1,
      isMounted: false,
    };
  },

  computed: {
    /** Array: Array of object of properties for each field */
    fieldProps() {
      return [
        { title: 'WHAT', key: 'lWhat' },
        {
          title: 'WHO',
          key: 'lWho',
          autocomplete: this.$store.getters['problem/radioUsers'](this.problem.id),
          autocompleteChipsType: AutocompleteChipsTypes.USER,
        },
        {
          title: 'WHERE',
          key: 'lWhere',
          autocomplete: treeAsArray(this.allZones).map(z => ({ id: z.id, label: z.name })),
          autocompleteChipsType: AutocompleteChipsTypes.ZONE,
        },
        { title: 'WHEN', key: 'lWhen' },
        { title: 'HOW', key: 'lHow' },
        { title: 'HOW_MUCH', key: 'lHowMuch' },
        { title: 'WHY', key: 'lWhy' },
      ];
    },
    /** Boolean: Whether problem reporting hints should be displayed on this page or not */
    displayHints() {
      return this.currentTeam.teamHintsProblemSheet;
    },
    /** Number: Percentage of trust of the problem description */
    problemTrust() {
      return getProblemTrustIndex(this.problemData);
    },
    ...mapState({
      allZones: state => state[GET_ZONES.stateKey] || [],
    }),

  },

  watch: {
    problem: {
      deep: true,
      handler() {
        if (this.problem && !isEqual(this.problem, this.problemData)) {
          this.problemData = cloneDeep(this.problem);
        }
      },
    },
  },

  created() {
    this.problemData = Object.assign({}, this.problem);
  },

  mounted() {
    this.isMounted = true;
  },

  methods: {
    safeString,
    /** Problem field was edited */
    editProblem(field, value) {
      value = value || this.problemData[field];
      const params = { id: this.problemData.id, [field]: value };
      this.$store.dispatch(`problem/${EDIT_PROBLEM}`, params).then(this.sendSocketUpdate);
    },
    /** Send a socket msg to notify teams that this problem sheet can be refreshed */
    sendSocketUpdate() {
      this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PROBLEM_SHEET, id: this.problem.id, socketCompany: 1 });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";
.problem-data-section {

  &__hint {
    margin-top: .5rem;
    opacity: 0.65;
  }

  .file-chip-list {

    .picture-carousel {
      margin-bottom: 0.5rem;

      &__current-picture img {
        max-height: 5rem;
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .problem-data-section {

  }
}
</style>
